// ---------------------------------------------------------------------------
// ResizeHandles — corner and edge handles for resizing selected elements.
//
// Shows 8 handles (4 corners + 4 edges) around a single selected element.
// Drag a handle to resize; updates the element's className with Tailwind
// arbitrary values (e.g. w-[200px], h-[150px]).
//
// z-index: 51 — just above selection overlay (50).
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { findElementInTree, findParentInTree } from '@/features/page-editor/store/canvas-slice'
import { toast } from '@/store/toast-store'
import { resolveVisibleElement } from '@/features/page-editor/lib/dom-utils'
import { setWidthPercent } from '@/features/page-editor/lib/class-utils'
import { redistributeSizes, equalDistribute } from '@/features/page-editor/lib/bounded-flex'

type Direction = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

interface HandlePos {
  top: number
  left: number
  width: number
  height: number
}

const HANDLE_SIZE = 8
const HALF = HANDLE_SIZE / 2
const MIN_SIZE = 20

const CURSORS: Record<Direction, string> = {
  nw: 'nwse-resize',
  n: 'ns-resize',
  ne: 'nesw-resize',
  e: 'ew-resize',
  se: 'nwse-resize',
  s: 'ns-resize',
  sw: 'nesw-resize',
  w: 'ew-resize',
}

export function ResizeHandles() {
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const isPreview = useEditorStore((s) => s.isPreview)
  const activeBreakpoint = useEditorStore((s) => s.activeBreakpoint)
  const zoom = useEditorStore((s) => s.zoom)
  const containerRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<HandlePos | null>(null)
  const posRef = useRef<HandlePos | null>(null)
  posRef.current = pos // keep ref in sync for event handlers
  const dragRef = useRef<{
    dir: Direction
    startX: number
    startY: number
    startPos: HandlePos
  } | null>(null)

  // Only show resize handles for single selection
  const singleId = selectedIds.length === 1 ? selectedIds[0] : null

  const updatePosition = useCallback(() => {
    if (!singleId || !selectedBlockName) {
      setPos(null)
      return
    }

    const canvasEl = containerRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    const domEl = canvasEl.querySelector(
      `[data-element-id="${singleId}"]`,
    ) as HTMLElement | null
    if (!domEl) {
      setPos(null)
      return
    }

    // Resolve to visible element for display:contents wrappers
    const visibleEl = resolveVisibleElement(domEl)

    // Use overlay container rect (not canvasEl) since overlays are positioned
    // inside <div className="relative"> within DeviceFrame, not the canvas viewport.
    // getBoundingClientRect() returns screen-space (post-zoom-transform) values,
    // but CSS top/left operate in local (pre-transform) space, so divide by zoom.
    const containerRect = containerRef.current!.getBoundingClientRect()
    const elRect = visibleEl.getBoundingClientRect()
    const z = zoom || 1

    setPos({
      top: (elRect.top - containerRect.top) / z,
      left: (elRect.left - containerRect.left) / z,
      width: elRect.width / z,
      height: elRect.height / z,
    })
  }, [singleId, selectedBlockName, activeBreakpoint, zoom])

  useEffect(() => {
    updatePosition()

    const canvasEl = containerRef.current?.closest('[data-testid="canvas-viewport"]')
    if (!canvasEl) return

    let rafId = 0
    const debouncedUpdate = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(updatePosition)
    }

    canvasEl.addEventListener('scroll', updatePosition, { passive: true })
    window.addEventListener('resize', updatePosition)
    const observer = new MutationObserver(debouncedUpdate)
    observer.observe(canvasEl, { childList: true, subtree: true })

    return () => {
      cancelAnimationFrame(rafId)
      canvasEl.removeEventListener('scroll', updatePosition)
      window.removeEventListener('resize', updatePosition)
      observer.disconnect()
    }
  }, [updatePosition])

  // Drag handlers — use posRef to avoid re-attaching listeners on every pos change
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current || !posRef.current) return
      e.preventDefault()

      const { dir, startX, startY, startPos } = dragRef.current
      // Mouse deltas are in screen-space; convert to local (pre-zoom) space
      const z = useEditorStore.getState().zoom || 1
      const dx = (e.clientX - startX) / z
      const dy = (e.clientY - startY) / z

      let newTop = startPos.top
      let newLeft = startPos.left
      let newWidth = startPos.width
      let newHeight = startPos.height

      // Horizontal resize
      if (dir.includes('e')) {
        newWidth = Math.max(MIN_SIZE, startPos.width + dx)
      }
      if (dir.includes('w')) {
        const dw = Math.min(dx, startPos.width - MIN_SIZE)
        newWidth = startPos.width - dw
        newLeft = startPos.left + dw
      }

      // Vertical resize
      if (dir.includes('s')) {
        newHeight = Math.max(MIN_SIZE, startPos.height + dy)
      }
      if (dir === 'n' || dir === 'nw' || dir === 'ne') {
        const dh = Math.min(dy, startPos.height - MIN_SIZE)
        newHeight = startPos.height - dh
        newTop = startPos.top + dh
      }

      setPos({ top: newTop, left: newLeft, width: newWidth, height: newHeight })
    }

    const handleMouseUp = () => {
      if (!dragRef.current || !singleId || !selectedBlockName) {
        dragRef.current = null
        return
      }

      // Apply the new size to the element's className
      const store = useEditorStore.getState()
      const block = store.blocks[selectedBlockName]
      const currentPos = posRef.current
      if (block && currentPos) {
        const el = findElementInTree(block.ui.root, singleId)
        if (el) {
          // Check if parent is a horizontal flex container — use % sizing
          const parentResult = findParentInTree(block.ui.root, singleId)
          const parentLayout = parentResult?.parent.layout
          const isHorizontalFlex =
            parentLayout?.type === 'flex' && parentLayout.direction === 'row'

          if (isHorizontalFlex && parentResult) {
            // %-based resize with sibling redistribution
            const canvasEl = containerRef.current?.closest('[data-testid="canvas-viewport"]')
            const parentDomRaw = canvasEl?.querySelector(
              `[data-element-id="${parentResult.parent.id}"]`,
            ) as HTMLElement | null
            const parentDom = parentDomRaw ? resolveVisibleElement(parentDomRaw) : null
            const z = store.zoom || 1
            const parentWidth = parentDom ? parentDom.getBoundingClientRect().width / z : currentPos.width

            const newPercent = Math.round((currentPos.width / parentWidth) * 100)
            const clampedPercent = Math.max(10, Math.min(90, newPercent))

            const siblings = parentResult.parent.children!
            // Get current sizes as percentages
            const currentSizes = siblings.map((child) => {
              const childDomRaw = canvasEl?.querySelector(
                `[data-element-id="${child.id}"]`,
              ) as HTMLElement | null
              const childDom = childDomRaw ? resolveVisibleElement(childDomRaw) : null
              if (childDom) {
                return Math.round((childDom.getBoundingClientRect().width / z / parentWidth) * 100)
              }
              return Math.round(100 / siblings.length)
            })

            const newSizes = redistributeSizes(currentSizes, parentResult.index, clampedPercent)

            // Update each sibling's width class
            siblings.forEach((child, i) => {
              const updatedClassName = setWidthPercent(child.className ?? '', newSizes[i])
              store.updateElement(selectedBlockName, child.id, {
                className: updatedClassName,
              })
            })

            toast.info(`Resized to ${clampedPercent}%`)
          } else {
            // px-based resize (default for non-flex or vertical flex)
            const newW = `w-[${Math.round(currentPos.width)}px]`
            const newH = `h-[${Math.round(currentPos.height)}px]`
            const cls = (el.className ?? '')
              .split(/\s+/)
              .filter(Boolean)
              .filter((c) => !c.startsWith('w-') && !c.startsWith('h-'))
            cls.push(newW, newH)
            store.updateElement(selectedBlockName, singleId, {
              className: cls.join(' '),
            })
            toast.info(`Resized to ${Math.round(currentPos.width)} × ${Math.round(currentPos.height)}`)
          }
        }
      }

      dragRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [singleId, selectedBlockName])

  const handleMouseDown = (e: React.MouseEvent, dir: Direction) => {
    if (!pos) return
    e.preventDefault()
    e.stopPropagation()
    dragRef.current = {
      dir,
      startX: e.clientX,
      startY: e.clientY,
      startPos: { ...pos },
    }
    document.body.style.cursor = CURSORS[dir]
    document.body.style.userSelect = 'none'
  }

  const showHandles = !isPreview && singleId && pos

  const handles: { dir: Direction; top: number; left: number }[] = pos
    ? [
        // Corners
        { dir: 'nw', top: pos.top - HALF, left: pos.left - HALF },
        { dir: 'ne', top: pos.top - HALF, left: pos.left + pos.width - HALF },
        { dir: 'sw', top: pos.top + pos.height - HALF, left: pos.left - HALF },
        { dir: 'se', top: pos.top + pos.height - HALF, left: pos.left + pos.width - HALF },
        // Edges
        { dir: 'n', top: pos.top - HALF, left: pos.left + pos.width / 2 - HALF },
        { dir: 's', top: pos.top + pos.height - HALF, left: pos.left + pos.width / 2 - HALF },
        { dir: 'w', top: pos.top + pos.height / 2 - HALF, left: pos.left - HALF },
        { dir: 'e', top: pos.top + pos.height / 2 - HALF, left: pos.left + pos.width - HALF },
      ]
    : []

  // Dimension label (shown during drag or always on selection)
  const isDragging = dragRef.current != null
  const dimLabel = pos
    ? `${Math.round(pos.width)} × ${Math.round(pos.height)}`
    : ''

  // Always render the container so containerRef is available for DOM queries
  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0"
      style={{ zIndex: 51 }}
      data-testid="resize-handles"
    >
      {showHandles && (
        <>
          {handles.map((h) => (
            <div
              key={h.dir}
              className="absolute rounded-[2px] border border-primary bg-background shadow-sm"
              style={{
                top: h.top,
                left: h.left,
                width: HANDLE_SIZE,
                height: HANDLE_SIZE,
                cursor: CURSORS[h.dir],
                pointerEvents: 'auto',
              }}
              onMouseDown={(e) => handleMouseDown(e, h.dir)}
              data-testid={`resize-handle-${h.dir}`}
            />
          ))}

          {/* Dimension label — shown below bottom edge */}
          <div
            className="absolute flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium leading-none whitespace-nowrap"
            style={{
              top: pos!.top + pos!.height + 6,
              left: pos!.left + pos!.width / 2,
              transform: 'translateX(-50%)',
              backgroundColor: isDragging
                ? 'hsl(var(--primary))'
                : 'hsl(var(--muted-foreground) / 0.75)',
              color: 'hsl(var(--primary-foreground))',
            }}
            data-testid="dimension-label"
          >
            {dimLabel}
          </div>
        </>
      )}
    </div>
  )
}
